import { useNavigate } from "@remix-run/react";
import { useCallback, useMemo } from "react";
import { Modal } from "react-daisyui";
import { useTranslation } from "react-i18next";

import IconChevronRight from "~/components/atoms/Icons/IconChevronRight";
import IconClose from "~/components/atoms/Icons/IconClose";
import Typo from "~/components/atoms/Typo";
import CardProject from "~/components/molecules/CardProject";
import { IProject } from "~/entities/project";
import { cn } from "~/utilities/cn";
import { Slug } from "~/utilities/enums/Slug";
import { ETypoVariant } from "~/utilities/enums/Typo";

export interface IRelatedPropertiesModalProps {
  open: boolean;
  projects: IProject[];
  onClose: () => void;
}

const RelatedPropertiesMobileModal: React.FC<IRelatedPropertiesModalProps> = ({
  open,
  projects,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderedProjects = useMemo(() => {
    return projects?.length > 1 ? (
      <div className="w-full pl-6">
        <div className="scroll-padding-6 no-scrollbar -ml-6 grid snap-x snap-mandatory snap-always grid-flow-col grid-cols-none gap-6 overflow-x-auto px-6 py-0 lg:auto-cols-[90%]">
          {projects.map((project, index) => (
            <CardProject
              full
              key={project.id + index}
              className="scroll-snap-start lg:auto-cols-[90%]"
              thumbnailClass="min-h-[136px]"
              project={project}
            />
          ))}
        </div>
      </div>
    ) : null;
  }, [projects]);

  return (
    <Modal.Legacy
      open={open}
      className={cn("hidden !bg-background px-0 lg:block")}
      onClickBackdrop={onClose}
    >
      <Modal.Header className="font-bold">
        <div className=" flex w-full flex-row justify-between px-6">
          <div className="flex flex-col gap-1">
            <Typo
              variant={ETypoVariant.HEADER_32}
              tag="h4"
              className="flex-1 text-left font-bold lg:text-header-20"
            >
              {t("related_properties.title")}
            </Typo>
            <Typo
              variant={ETypoVariant.HEADER_16}
              tag="p"
              className="whitespace-nowrap text-left"
            >
              {t("related_properties.some_properties_interested_in")}
            </Typo>
          </div>
          <IconClose onClick={handleClose} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="overflow-hidden">{renderedProjects}</div>
        <div className=" flex items-center justify-center p-6 pb-3">
          <button
            className="flex cursor-pointer items-center gap-1.5"
            onClick={() => {
              onClose();
              if (location.pathname !== Slug.PROJECTS) {
                navigate(Slug.PROJECTS);
              }
            }}
          >
            <Typo
              variant={ETypoVariant.HEADER_16}
              tag="p"
              className="font-bold"
            >
              {t("related_properties.more_project_available")}
            </Typo>
            <IconChevronRight />
          </button>
        </div>
      </Modal.Body>
    </Modal.Legacy>
  );
};

export default RelatedPropertiesMobileModal;
